/**
 * THE VALLEY BEHIND EVERYTHING (Living Valley, 2026-10-11).
 *
 * The parent app used to be cards on a pale page with the valley as one card
 * among them. Now the painting IS the page: it fills the window behind every
 * parent screen, at the family's own stage and the device's own hour, and the
 * UI floats on frosted paper over it.
 *
 * Which painting depends on the balance, which only Home knows, while the
 * page frame (Shell, in App.tsx) is drawn above Home. So the frame owns a
 * small context and Home reports the balance into it. Before Home has
 * reported anything -- sign-in, the picker, the unlock screen -- the valley is
 * shown at 'growing', which is the friendliest stage that does not claim
 * anything about anybody's money.
 *
 * Same files as the Valley component: same origin, AVIF then WebP, one
 * painting downloaded. Nothing here makes a request anywhere else.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { sceneFor, DEFAULT_TARGET } from '../valley/scene';
import { assetUrl } from '../valley/sprites';

interface BackdropValue {
  balance: number;
  target: number;
}

const FRIENDLY: BackdropValue = { balance: Math.round(DEFAULT_TARGET * 0.5), target: DEFAULT_TARGET };

const Ctx = createContext<{ value: BackdropValue; set: (v: BackdropValue) => void } | null>(null);

export function BackdropProvider({ children }: { children: ReactNode }) {
  const [value, set] = useState<BackdropValue>(FRIENDLY);
  return <Ctx.Provider value={{ value, set }}>{children}</Ctx.Provider>;
}

/** Called by Home whenever the family's balance or target changes. */
export function useReportBackdrop(balance: number | undefined, target: number | undefined): void {
  const ctx = useContext(Ctx);
  useEffect(() => {
    if (!ctx || balance === undefined) return;
    const next = { balance, target: target || DEFAULT_TARGET };
    if (next.balance !== ctx.value.balance || next.target !== ctx.value.target) ctx.set(next);
    // Only the numbers matter; ctx identity changes on every set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, target]);
}

function measure() {
  return { hour: new Date().getHours(), width: window.innerWidth, height: window.innerHeight };
}

/**
 * The fixed, full-window painting plus its scrim. Decorative: the stage is
 * said in words wherever it matters (the Home eyebrow), so this is aria-hidden
 * and has an empty alt.
 */
export function ValleyBackdrop({ quiet = false }: { quiet?: boolean }) {
  const ctx = useContext(Ctx);
  const value = ctx?.value ?? FRIENDLY;
  const [view, setView] = useState(measure);

  useEffect(() => {
    const onResize = () => setView(measure());
    window.addEventListener('resize', onResize);
    // The hour changes too; once every ten minutes is plenty for dusk.
    const timer = window.setInterval(onResize, 10 * 60 * 1000);
    return () => {
      window.removeEventListener('resize', onResize);
      window.clearInterval(timer);
    };
  }, []);

  const scene = sceneFor(value.balance, { ...view, target: value.target });
  return (
    <div className={quiet ? 'lv-backdrop quiet' : 'lv-backdrop'} aria-hidden="true" data-stage={scene.stage}>
      <picture>
        <source srcSet={assetUrl(scene.name, 'avif')} type="image/avif" />
        <img src={assetUrl(scene.name, 'webp')} alt="" decoding="async" draggable={false} />
      </picture>
      <div className="lv-scrim" />
      {/* Two fireflies, no more: alive, never busy. Off under reduced motion. */}
      <i className="lv-firefly" />
      <i className="lv-firefly two" />
    </div>
  );
}
